/**
 * Subscription plan catalog + contribution credit rules.
 * Monthly: Map ($11/mo, map only) or VRC Member ($22/mo, Map + Calendar).
 * Yearly VRC Member ($220/yr) is offered only while the yearlyPricing flag is on.
 * Approved locations earn Credits (banked; exchange to Wallet monthly).
 */

/** 1 Credit earned per typical approval when using default economy. */
export const CREDITS_PER_APPROVAL_UNIT = 1;

/*
 * The subscription discount cap and Credit → $ rate come from the credit economy
 * (system/creditEconomy): see maxSubscriptionDiscountCents / subscriptionDiscountForCredits
 * in src/utils/creditEconomyConfig.js (mirrored in functions/creditEconomy.js).
 */

export const PLAN_IDS = {
  /** Map + Calendar, monthly (existing members; was $33, now $22). */
  VRC_MEMBER: 'vrc_member',
  MAP_ONLY: 'map_only',
  VRC_MEMBER_YEARLY: 'vrc_member_yearly',
  /** @deprecated legacy Stripe / admin values — treated as VRC Member */
  CALENDAR_ONLY: 'calendar_only',
  MAP_CALENDAR: 'map_calendar',
};

const MEMBER_FEATURES = [
  'Full calendar access',
  'Tour map / planner access',
  'Earn 1 Credit per approved map contribution',
  'Cancel anytime',
];

export const SUBSCRIPTION_PLANS = {
  [PLAN_IDS.MAP_ONLY]: {
    id: PLAN_IDS.MAP_ONLY,
    name: 'Map',
    priceCents: 1100,
    priceLabel: '$11',
    periodLabel: '/month',
    interval: 'month',
    allowsContributionDiscount: false,
    includesCalendar: false,
    includesMap: true,
    features: [
      'Tour map / planner access',
      'Earn 1 Credit per approved map contribution',
      'Cancel anytime',
    ],
    blurb: '$11/month — the tour map. Credits bank in your account.',
  },
  [PLAN_IDS.VRC_MEMBER]: {
    id: PLAN_IDS.VRC_MEMBER,
    name: 'Calendar + Map',
    priceCents: 2200,
    priceLabel: '$22',
    periodLabel: '/month',
    interval: 'month',
    allowsContributionDiscount: false,
    includesCalendar: true,
    includesMap: true,
    features: MEMBER_FEATURES,
    blurb: '$22/month — Map and Calendar included. Credits bank in your account.',
  },
  [PLAN_IDS.VRC_MEMBER_YEARLY]: {
    id: PLAN_IDS.VRC_MEMBER_YEARLY,
    name: 'Calendar + Map (yearly)',
    priceCents: 22000,
    priceLabel: '$220',
    periodLabel: '/year',
    interval: 'year',
    yearly: true,
    allowsContributionDiscount: false,
    includesCalendar: true,
    includesMap: true,
    features: MEMBER_FEATURES,
    blurb: '$220/year — Map and Calendar included, two months free.',
  },
};

/** Plans on /plans. Yearly shows only when the yearlyPricing feature flag is on. */
export function getVisiblePlanList({ yearlyPricing = false } = {}) {
  return [
    SUBSCRIPTION_PLANS[PLAN_IDS.MAP_ONLY],
    SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER],
    ...(yearlyPricing ? [SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER_YEARLY]] : []),
  ];
}

export const SUBSCRIPTION_PLAN_LIST = getVisiblePlanList();

/** Tour admin prepay per accepted crew member (same as member price). */
export const TOUR_PARTY_CREW_SEAT_PRICE_CENTS = SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER].priceCents;
export const TOUR_PARTY_BILLING_COLLECTION = 'tourPartyBilling';

export const CONTRIBUTION_SOURCES = [
  {
    collection: 'resource_submissions',
    userIdFields: ['userId'],
    approvedAtField: 'approvedAt',
  },
  {
    collection: 'venue_spec_flags',
    userIdFields: ['userId'],
    approvedAtField: 'approvedAt',
  },
  {
    collection: 'new_venues',
    userIdFields: ['userId', 'createdBy'],
    approvedAtField: 'approvedAt',
  },
];

export function normalizePlanId(planId) {
  if (!planId) return null;
  if (SUBSCRIPTION_PLANS[planId]) return planId;
  if (planId === PLAN_IDS.CALENDAR_ONLY || planId === PLAN_IDS.MAP_CALENDAR) {
    return PLAN_IDS.VRC_MEMBER;
  }
  return planId;
}

export function getPlanById(planId) {
  return SUBSCRIPTION_PLANS[normalizePlanId(planId)] || null;
}

/** Approved contributions this cycle (informational; the discount comes from banked Credits). */
export function clampApprovedLocationCount(count) {
  return Math.max(0, Math.floor(Number(count) || 0));
}

export function computeDiscountCents(approvedCount) {
  return clampApprovedLocationCount(approvedCount) * CREDITS_PER_APPROVAL_UNIT;
}

/** Legacy helper — effective member price is the fixed VRC Member price. */
export function computeMapCalendarPriceCents(_approvedCount = 0) {
  return SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER].priceCents;
}

export function computeEffectivePriceCents(planId, _approvedCount = 0) {
  const plan = getPlanById(planId);
  if (!plan) return null;
  return plan.priceCents;
}

export function formatCentsAsDollars(cents) {
  const value = (Number(cents) || 0) / 100;
  return `$${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}`;
}

export function getContributionDiscountSummary(approvedCount = 0) {
  const capped = clampApprovedLocationCount(approvedCount);
  const discountCents = computeDiscountCents(capped);
  const memberPrice = SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER].priceCents;
  return {
    approvedCount: Math.max(0, Math.floor(Number(approvedCount) || 0)),
    cappedCount: capped,
    discountCents,
    discountDollarsLabel: formatCentsAsDollars(discountCents),
    effectivePriceCents: memberPrice,
    effectivePriceLabel: formatCentsAsDollars(memberPrice),
    basePriceLabel: formatCentsAsDollars(memberPrice),
  };
}
